import { Box, Stack, Typography } from "@mui/material";
import { type FC } from "react";
import {
  type EngagementBreakdownItem,
  useEngagementBreakdownSummary,
} from "./use-engagement-breakdown-summary";

const fallbackBreakdown: EngagementBreakdownItem[] = [
  { clicked: 0, delivered: 0, label: "", percentage: 0 },
];

export const EngagementBreakdownData: FC<{
  breakdown: EngagementBreakdownItem[];
}> = ({ breakdown }) => {
  const summaryBreakdown = breakdown.length > 0 ? breakdown : fallbackBreakdown;
  const { total, sortedBreakdown } = useEngagementBreakdownSummary(summaryBreakdown);

  if (sortedBreakdown.length === 0) return null;

  const mostEngaged = sortedBreakdown[0]!;
  const leastEngaged = sortedBreakdown[sortedBreakdown.length - 1] ?? mostEngaged;
  const averagePercentage = Math.round(total / summaryBreakdown.length);

  return (
    <>
      <Stack direction="row" spacing={3}>
        <EngagementSummary
          title="Most engaged"
          item={mostEngaged}
          backgroundColor="#E7F8F6"
          textColor="#084A42"
        />
        <EngagementSummary
          title="Least engaged"
          item={leastEngaged}
          backgroundColor="#FFF7E9"
          textColor="#66460D"
        />
      </Stack>
      <EngagementBreakdownChart data={sortedBreakdown} average={averagePercentage} />
    </>
  );
};

const EngagementSummary: FC<{
  title: string;
  item: EngagementBreakdownItem;
  backgroundColor: string;
  textColor: string;
}> = ({ title, item, backgroundColor, textColor }) => (
  <Box sx={{ flex: 1, backgroundColor, borderRadius: 1, px: 3, py: 2 }}>
    <Stack spacing={1}>
      <Typography
        variant="overline"
        sx={{ color: textColor, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}
      >
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: textColor }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {item.label}
        </Box>{" "}
        {item.clicked}/{item.delivered} ({item.percentage}%)
      </Typography>
    </Stack>
  </Box>
);

const EngagementBreakdownChart: FC<{
  data: EngagementBreakdownItem[];
  average: number;
}> = ({ data, average }) => (
  <Box sx={{ position: "relative", pt: 3 }}>
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        left: `${average}%`,
        position: "absolute",
        top: 0,
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
      }}
    >
      Average ({average}%)
    </Typography>
    <Box
      sx={{
        borderLeft: "2px dashed #5048E5",
        bottom: 0,
        left: `${average}%`,
        position: "absolute",
        top: 26,
        zIndex: 1,
      }}
    />
    <Stack spacing={1}>
      {data.map((item) => (
        <Box
          key={item.label}
          sx={{
            bgcolor: "#C2E5C4",
            borderRadius: 1,
            height: 44,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              bgcolor: "#7BC67E",
              borderRadius: 1,
              height: "100%",
              width: `${item.percentage}%`,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: "#084A42",
              fontWeight: 400,
              left: 1.5,
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              pl: 2,
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {item.label}
            </Box>{" "}
            {item.clicked}/{item.delivered} ({item.percentage}%)
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);
